/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The comparer's filter rule, in one place: which offers a set of filter
 * inputs keeps (docs/plan-vergelijker-resultaten-2026-09-30.md §12.4 B5).
 *
 * Extracted rule for rule from ComparisonEngine.tsx so that the list and
 * the phone filter sheet's live count ("Toon 25 resultaten") are the SAME
 * function of the same inputs and cannot disagree: the engine narrows what it
 * renders with `applyComparerFilters`, the sheet counts its draft with it.
 *
 * Three steps, each pure:
 *
 *   read    what the address says. `parseSmartChargingFilter` and
 *           `parseTermsFilter` here; the audience, the volume and the looptijd
 *           are src/lib/comparerParams.ts's. The score filter's `?minRating=`
 *           is read by nothing since the ratings left (2026-10-14): an old link
 *           that carries it shows the unfiltered list.
 *   gate    a filter whose control is not on screen narrows nothing
 *           (`effectiveComparerFilters`): a Voorwaarden value exists only while
 *           some offer earns it. Measured over the WHOLE data set, never the
 *           filtered view — gating on the view would let a filter that empties
 *           the list delete the very control needed to undo it. Ignored, not
 *           rewritten: the parameter stays in the address and starts working
 *           again the day the data can answer it.
 *   narrow  `applyComparerFilters`: the rows the gated inputs keep, in the
 *           order they came in. It never sorts — src/lib/comparerSort.ts does,
 *           and the ranking disclosure above the list stays true because a
 *           filter only ever narrows.
 *
 * Data identifiers, never labels: 'Alle', 'Particulier', '1 jaar', 'all' and
 * the feature codes are matched against the provider database, so they stay
 * Dutch in every language (CLAUDE.md, "URL param values").
 *
 * Pure on purpose (the comparerParams.ts pattern): no React, no router, no
 * i18n.
 */

import type { EREProvider } from '../types';
import {
  FEATURE_CODES,
  hasFeature,
  isFeatureCode,
  type FeatureCode,
  type FeatureInput,
} from './providerFeatures';
import { DEFAULT_DURATION_SELECTION, parseDurationSelection } from './durationOptions';

/**
 * The smart-charger filter, user-centric: 'has' means the visitor owns a smart
 * charger (no narrowing — they can use any provider), 'none' keeps only the
 * offers that do not require one.
 */
export type SmartChargingFilter = 'all' | 'has' | 'none';

/** The "Voorwaarden" filter: one derived feature (src/lib/providerFeatures.ts) or all. */
export type TermsFilter = FeatureCode | 'all';

/** Every input that narrows the list. The volume is not one: it ranks, it never filters. */
export interface ComparerFilters {
  /** 'Alle' | 'Particulier' | 'Zakelijk' — compared straight against `targetAudience`. */
  audience: string;
  /**
   * 'Alle' or one looptijd or a comma list of them (the multi-select,
   * src/lib/durationOptions.ts), compared case-insensitively; an offer with no
   * known looptijd never matches a selection.
   */
  contractDuration: string;
  smartCharging: SmartChargingFilter;
  terms: TermsFilter;
}

/** What the whole data set can answer — whether a control exists at all. */
export interface ComparerFilterGates {
  /** The Voorwaarden codes at least one offer earns, in FEATURE_CODES order. */
  features: readonly FeatureCode[];
}

/** A row as the filter reads it — the fields it looks at, nothing else. */
export type ComparerFilterRow = Pick<
  EREProvider,
  'targetAudience' | 'contractDuration' | 'smartChargingRequired'
> &
  FeatureInput;

/**
 * The filters "wissen" resets, in the results bar's order. The audience is not
 * among them: it is the visitor's own segment, and a reset that moved a
 * zakelijke bezoeker into consumer results would be a change nobody asked for.
 */
export const RESETTABLE_FILTERS = ['contractDuration', 'smartCharging', 'terms'] as const;
export type ResettableFilter = (typeof RESETTABLE_FILTERS)[number];

/** What each resettable filter reads on a clean address. */
export const FILTER_DEFAULTS: Readonly<{
  contractDuration: string;
  smartCharging: SmartChargingFilter;
  terms: TermsFilter;
}> = {
  contractDuration: DEFAULT_DURATION_SELECTION,
  smartCharging: 'all',
  terms: 'all',
};

/**
 * `?smartCharging=`. The older provider-perspective values ('required',
 * 'not_required') still resolve to the user-centric option, so bookmarks from
 * before the rename keep their meaning; anything else is 'all'.
 */
export function parseSmartChargingFilter(raw: string | null | undefined): SmartChargingFilter {
  if (raw === 'has' || raw === 'required') return 'has';
  if (raw === 'none' || raw === 'not_required') return 'none';
  return 'all';
}

/** `?terms=`, whitelisted: a value off FEATURE_CODES (a typo, an old link) filters nothing. */
export function parseTermsFilter(raw: string | null | undefined): TermsFilter {
  return isFeatureCode(raw) ? raw : 'all';
}

/** The Voorwaarden codes some row earns: the options the filter may offer. */
export function availableFeatureCodes(rows: ReadonlyArray<FeatureInput>): FeatureCode[] {
  return FEATURE_CODES.filter((code) => rows.some((row) => hasFeature(row, code)));
}

/** The inputs with every filter whose control is not on screen switched off. */
export function effectiveComparerFilters(
  filters: ComparerFilters,
  gates: ComparerFilterGates
): ComparerFilters {
  return {
    ...filters,
    terms:
      filters.terms !== 'all' && gates.features.includes(filters.terms) ? filters.terms : 'all',
  };
}

/**
 * The resettable filters away from their default: one applied-filter chip
 * each, and their number is the "(n)" on the filter button and on "wissen".
 * Pass the EFFECTIVE filters — an ignored Voorwaarden value narrows nothing,
 * so counting it would announce a filter over an unfiltered list.
 */
export function activeComparerFilters(filters: ComparerFilters): ResettableFilter[] {
  return RESETTABLE_FILTERS.filter((key) => filters[key] !== FILTER_DEFAULTS[key]);
}

/**
 * The rows the filters keep, in the order given. Pass the EFFECTIVE filters
 * (`effectiveComparerFilters`), as the engine and the sheet both do.
 */
export function applyComparerFilters<T extends ComparerFilterRow>(
  rows: readonly T[],
  filters: ComparerFilters
): T[] {
  const durations = parseDurationSelection(filters.contractDuration)?.map((d) => d.toLowerCase());
  return rows.filter((p) => {
    if (filters.audience !== 'Alle' && p.targetAudience !== filters.audience) return false;
    if (durations) {
      // A selection excludes the offers whose looptijd is unknown.
      if (!p.contractDuration) return false;
      if (!durations.includes(p.contractDuration.toLowerCase())) return false;
    }
    // 'has' narrows nothing: a visitor with a smart charger can use every
    // offer, including the ones that require one.
    if (filters.smartCharging === 'none' && p.smartChargingRequired !== false) return false;
    if (filters.terms !== 'all' && !hasFeature(p, filters.terms)) return false;
    return true;
  });
}
